import _ from 'lodash';
import postal from 'postal';
import React, {useContext, useEffect, useRef, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router';
import ListJobNames, {ListJob} from './listJobNames';
import constants from '../../constants';
import useJobMenu from '../../hooks/useJobMenu';
import ScreenTitleBar from '../../uiControls/screens/ScreenTitleBar';
import CloseButton from '../../ui/CloseButton';
import useFilterBar from '../../hooks/useFilterBar';
import useFunctionKeys from '../../hooks/useFunctionKeys';
import useListData from '../../hooks/useListData';
import useGetEditedId from '../../hooks/useGetEditedId';
import {ApplicableTableSettings, createColumnFocusListHierarchy, TableColumn} from '../../tableSettings';
import SmartTable, {SmartTableHandle} from '../../uiControls/SmartTable';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../ApplicationContext';

import JobListViewContext, {IJobListViewContext} from './JobListViewContext';
import useSystemSettings from '../../hooks/useSystemSettings';
import useSuntekSettings from '../../hooks/useSuntekSettings';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';
import ToggleButton from '../../ui/ToggleButton';
import { isFilterObjectEmpty } from '../../filtering';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import BlackInverseButton from '../../ui/BlackInverseButton';

import excelIcon from '../../img/excel_black.svg';
import excelIconGreen from '../../img/excel_green.svg';
import { exportDomTableIntoExcel } from '../../excel';
import { ListOrderBy, ViewUrl } from './AbstractListView';
import { XCellType } from '../../framework.core/XExportColumn';

import { compareIso } from '../../dateUtils';
import PrimaryButton from '../../ui/PrimaryButton';
import useStorage from '../../hooks/useStorage';
import config from '../../config';
import { useAppDispatch } from '../../app/hooks';
import fastStorage from '../../fastStorage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { useContextMenu } from '../../hooks/useContextMenu';
import { ScrollMemory } from '../../ui/tables/useTableNavigation';
import { getDecimal } from '../../util';
import { JobNames } from '../../framework.core/JobNames';
import { StatusBackColor } from './OrchidBaroListView';


const AllowedOpTypes = _.keys(constants.operations);

type JobListViewParams = {
  jobName: string;
  opType: string|undefined;
  parentOp: string|undefined;
};

/**
 * Gets operation name from property name (if exists.) For example oceanImport is mapped to 'Ocean Import'
 *
 * @param {(string|undefined)} opType
 * @return {*}  {(string|null)}
 */
function getOperationNameFromOpType(opType:string|undefined):string|null {
  if (!opType) {
    return null;
  }

  if (!AllowedOpTypes.some(k => k === opType)) {
    throw new Error(`Invalid opType. Must be either undefined or one of: [${AllowedOpTypes.join(', ')}]`)
  }

  return constants.operations[opType];
}

/**
 * Gets job object from name extracted from route
 *
 * @param {string} name
 * @return {*}  {Job}
 */
export function getJobFromName(name:string):ListJob {
  const result = ListJobNames[name]
  if (!result) {
    throw new Error('Invalid route');
  }
  return result;
}

interface ListSortResult {
  sorted: boolean,
  data: Array<any>,
}

function getColumnDataType(c: TableColumn): XCellType {
  return c.filterTypeV2 ?? XCellType.String;
}

function sortListData(data: Array<any>, orderBy: ListOrderBy|null, columnSettings: Array<TableColumn>): ListSortResult {
  if (!orderBy) {
    return {sorted: false, data: []};
  }

  const sortCol = columnSettings.find(x => x.accessor === orderBy.orderBy);
  if (!sortCol) {
    return {sorted: false, data: []};
  }

  const dataType = getColumnDataType(sortCol);

  const comparer = (a: any, b:any): number => {
    const left = orderBy.orderByAscending ? a[sortCol.accessor] : b[sortCol.accessor];
    const right = orderBy.orderByAscending ? b[sortCol.accessor] : a[sortCol.accessor];

    if (dataType === XCellType.Double) {
      return (left ?? 0) - (right ?? 0);
    }
    if (dataType === XCellType.Bool || dataType === XCellType.StrBool) {
      if (left === right) {
        return 0;
      }
      return left && !right ? 1 : -1;
    }
    if (dataType === XCellType.Date || dataType === XCellType.DateTime) {
      if (!left) {
        return !right ? 0 : -1;
      }
      if (!right) {
        return 1;
      }

      return compareIso(left as string, right as string);
    }

    const leftString:string = left as string ?? '';
    const rightString:string = right as string ?? '';

    return leftString.localeCompare(rightString);
  }

  const result = [...data];
  result.sort(comparer);

  return {sorted: true, data: result};
}

/**
 * Generic Job List View Component. Internally it constructs concrete class with needed implementations to display on the screen.
 * Implementation is constructed based on route params.
 *
 * @return {*} 
 */
function JobListViewImpl({jobName, opType, parentOp}: JobListViewParams) {
  // const listScreenSettings = useAppSelector(state => state.listScreenSettings);
   const dispatch = useAppDispatch();

  const operationName = getOperationNameFromOpType(opType);

  const job = getJobFromName(jobName);
  const listView = job.generateListView();

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const getSavedEntity = useGetEditedId();

  const appContext = useContext(ApplicationContext);
  const systemSettings = useSystemSettings();
  const suntekSettings = useSuntekSettings();

  const title = listView.getListViewTitle(opType);

  listView.setSystemSettings(systemSettings);
  listView.setSuntekSettings(suntekSettings);

  const listDomId = `tbl-list-${jobName}-${opType ?? 'common'}${listView.getTableDomIdSuffix()}`;
  const listDomWrapperId = `tbl-list-wrapper-${jobName}-${opType ?? 'common'}${listView.getTableDomIdSuffix()}`;

  const history = useHistory();

  const { FilterButton, search, clearFilters, clearFiltersFromStorage, /*setOrderBy,*/ setFilterForScreenExternally, setFilterForSearchModel, openedByF6, f6Origin, f6Query, setListFilterType, setListFilterType2 } = useFilterBar(
    listView.getTableSettings(opType, systemSettings, suntekSettings),
    listView.getCustomFilterRenderers(opType, systemSettings, suntekSettings),
    listView.getDefaultOrderBy(),
    '',
    operationName === 'My Event',
  );
  useFunctionKeys({onEsc: scheduleEscProcess});
  
  const parentOpName = getOperationNameFromOpType(parentOp);
  const noOperationSuffixMenu = (!!parentOpName && parentOpName !== 'Other Shipment') || opType === 'accounting' || (opType==='otherShipment' && jobName === 'otherShipment');
  const { canGoBack, goBack, 
    hasOpenedScreenStartingWithUrl, hasOpenedScreenStartingWithUrlWithoutMe, 
    hasOpenedScreenInAnyOperationStartingWithUrl, hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe, 
    getOpenedScreenInAnyOperationStartingWithUrl, 
    moveToOpenedScreenInAnyOperationStartingWithUrl }
    = useJobMenu(title, operationName, null, false, cleanup, null, { inputId: '', isLoading: false }, noOperationSuffixMenu, parentOpName);

  const [externalReloadRequest, setExternalReloadRequest] = useState('');
  const [initialized, setInitialized] = useState(false);
  const headerClickRef = useRef(true);

  const {onCellRightClick, RightClickContextMenu} = useContextMenu(listView);
  const {
    data,
    dataRef,
    setData,
    setSelectedId,
    getSelectedIndex,
    clearListData,
    selectTableRow,
    triggerTableSearch,
    currentCount,
    getSelectedRow,
    doSelectTableCellAndNotify,
  }
    = useListData(listDomId, onRowDoubleClick, createColumnFocusListHierarchy(listView.getTableSettings(opType, systemSettings, suntekSettings)), onCellRightClick, initialized, () => { headerClickRef.current = true });

  const tableRef = useRef<SmartTableHandle>(null);
  const [tick, setTick] = useState(0);

  const currentUrl = `${history.location.pathname}${history.location.search}`;
  //const infoAreaModelKey = `info-area-${currentUrl}`;
  const hideLogHistoryModelKey = `hide-log-${currentUrl}`;

  // let initializeInfoAreaValue = 'none';
  // if (appContext.appInfoAreaContent !== 'none') {
  //   if (appContext.appInfoAreaContent === 'memo' && !listView.hasMemo()) {
  //     initializeInfoAreaValue = 'none';
  //   } else {
  //     initializeInfoAreaValue = appContext.appInfoAreaContent;
  //   }
  // }

  //const [infoAreaContent, _setInfoAreaContent, deleteInfoAreaContent, infoAreaContentRef] = useTypedFastStorage<string>(infoAreaModelKey, initializeInfoAreaValue);
  const [listOrderBy, setListOrderBy, deleteListOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-list-data-${listDomId}-order-by`, listView.getDefaultOrderBy());
  const [hideLogHistory, , deleteHideLogHistory] = useStorage(hideLogHistoryModelKey, false);

  const [escScheduled, setEscScheduled] = useState(false);
  const [rowExtrasId, setRowExtrasId] = useState<number|null>(null);
  const clickedCellRef = useRef(null); 
  const [trackingRequestEmailList, setTrackingRequestEmailList] = useState<string|undefined>(undefined);
  const [recogModalData, setRecogModalData] = useState<{ isSet: boolean, isOpen: boolean }>({ isSet: false, isOpen: false });

  const selectedIndex = getSelectedIndex();
  const hasSelectedRow = selectedIndex >= 0;
  const selectedElement = hasSelectedRow ? data[selectedIndex] : null;
  const [viewPopupRow, setViewPopupRow] = useState<any|undefined>(undefined);
  const isMouseOverRef = useRef(false);
  const [listFilterOrder, setListFilterOrder] = useState(false);

  let reloadSubscription:ISubscriptionDefinition<any>|null = null;

  const jobListViewContext:IJobListViewContext = {
    selectedElement,
    hasOpenedScreenStartingWithUrl,
    hasOpenedScreenStartingWithUrlWithoutMe,
    hasOpenedScreenInAnyOperationStartingWithUrl,
    hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getOpenedScreenInAnyOperationStartingWithUrl,
    moveToOpenedScreenInAnyOperationStartingWithUrl,
    reloadData: reloadData,
    triggerTableSearch,
    setFilterForScreenExternally,
    setFilterForSearchModel,
    // infoAreaContent: listScreenSettings.showMemo ? 'memo' : 'none',
    //infoAreaContent: infoAreaContent ?? 'none',
    infoAreaContent: (listView.hasMemo() ? (appContext.appInfoAreaContent ?? 'none') : 'none'),
    setInfoAreaContent,
    updateDataRow,
    openedByF6,
    f6Origin,
    f6Query,
    rowExtrasId,
    setRowExtrasId,
    setClickedCell: (data) => { clickedCellRef.current = data },
    setData,
    data,
    viewPopupRow,
    dismissPopupRow: () => setViewPopupRow(undefined),
    showPopupRow,
    opType,
    search,
    setListFilterType,
    trackingRequestEmailList,
    setTrackingRequestEmailList,
    recogModalData,
    setRecogModalData,
    setListOrderBy,
    setListFilterOrder,
    setListFilterType2,
  };

  useEffect(() => {
    subscribeChannels();
    if (!data || data.length === 0) loadDataWithCurrentFilterSettings();
    else headerClickRef.current = false;

    return () => {
      unsubscribeChannels();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  useEffect(() => {
    if (!escScheduled) {
      return;
    }
    closeScreen();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [escScheduled]
  );

  useDeepCompareEffect(() => {
    setTick(tick + 1);
    refreshUpdatedEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    const isEmptyFilter = isFilterObjectEmpty(search);
    if (!search || !initialized || (!search.isSearchByReport && isEmptyFilter && search.version === 1 && search.listType === undefined)) {
      return;
    }
    if (isEmptyFilter) {
      setSelectedId(null);
    }
    else {
      if (ScrollMemory[listDomId])
        ScrollMemory[listDomId].forceSetDisplayRows = false;
    }
    reloadData();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (initialized && data.length > 0 && selectedIndex !== -1) {
      setTimeout(() => {
        selectTableRow(selectedIndex, headerClickRef.current);  
      }, 10);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIndex, initialized, tick]
  );

  useEffect(() => {
    if (externalReloadRequest === '') {
      return;
    }

    loadDataWithCurrentFilterSettings();

    setExternalReloadRequest('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalReloadRequest]);

  function reloadData(){
    headerClickRef.current = false;
    loadDataWithCurrentFilterSettings();
  }

  function subscribeChannels() {
    reloadSubscription = postal.subscribe({
      channel: 'ifms-job-list',
      topic: 'ifms-job-list.reload',
      callback: () => {
        setExternalReloadRequest('process');
      },
    });
  }

  function unsubscribeChannels() {
    if (reloadSubscription) {
      postal.unsubscribe(reloadSubscription);
    }
  }

  /**
   * Sets info area content type. Info area is rendering is defined in concrete implementation of listView instance if it is need.
   * Job list view just keeps track of currently active info area section.
   *
   * @param {string} c
   */
  function setInfoAreaContent(c:string) {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }
    // Currently no op - info area is no longer 'per list screen

    appContext.setAppInfoAreaContent(c);
    if (c === 'memo' && !listView.hasMemo()) return;
    // if (infoAreaContentRef.current === c) {
    //   _setInfoAreaContent('none');
    //   // infoAreaContentRef.current = 'none';
    // } else {
    //   _setInfoAreaContent(c);
    //   // infoAreaContentRef.current = c;
    // }
  }

  /**
   * Loads saved entity from edit/new screen and refresh in list (in case of edit),
   * or add to top (in case of new)
   *
   */
  async function refreshUpdatedEntity() {
    const editedEntity = getSavedEntity();
    if (editedEntity.isDelete) {
      // if (dataRef.current) {
      //   const newData = dataRef.current.filter(x => x.id !== parseInt(editedEntity.editedId ?? ''));
      //   setData(newData, newData.length);
      // }
      loadDataWithCurrentFilterSettings();
      return;
    }

    if (!editedEntity || !editedEntity.editedId) {
      return;
    }

    if (editedEntity.refreshAll) {
      loadDataWithCurrentFilterSettings();
      
      setSelectedId(editedEntity.editedId);
      return;
    }
    
    // let newlyUpdatedElementIndex: number = 0;
    // const job = getJobFromName(jobName);

    if (!editedEntity.isNew) {
      // const dataFetchResult = await listView.fetchAndFormatData(appContext.api, { ...initialSearch, ...{ general1: 'id', generalText1: editedEntity.editedId ?? '', startWithEntry:true } }, opType);

      // if (!dataFetchResult || !dataFetchResult.data || dataFetchResult.data.length <= 0) {
      //   return;
      // }

      // newlyUpdatedElementIndex = dataFetchResult.data[0].id;
      // const oriDataIndex = data.findIndex(x => x.id === newlyUpdatedElementIndex);
      
      // data[oriDataIndex] = dataFetchResult.data[0];

      // setData(data, data.length);
    }
    else {
      // const queryOpType = editedEntity.queryOpType;
      
      // const listView = job.generateListView();
      // const tempList = await listView.fetchAndFormatData(appContext.api, initialSearch, _.isEmpty(queryOpType) ? undefined : queryOpType);

      // const tempData = data;

      // //if (tempData.length === appContext.defaultPageSize) tempData.splice(tempData.length - 1); //delete last data

      // const insertItem = tempList.data.filter(cur => !tempData.some(x => x.id === cur.id));

      // if (insertItem.length === 0)
      //   tempData.unshift(tempList.data[0]); //add data at first index
      // else
      //   tempData.unshift(insertItem[0]);

      // setData(tempData, tempData.length);
      
      setSelectedId(data[0]?.id);
    }
  }

  function updateDataRow(row: any, insertIfNotExist: boolean = false) {
    if (!row || !row.id) {
      return;
    }
    headerClickRef.current = true;

    const newData = [...dataRef.current!];
    const editIndex = newData.findIndex(x => x.id === row.id);
    if (editIndex >= 0) {
      newData[editIndex] = row;
      setData(newData, currentCount);
    } else {
      if (insertIfNotExist) {
        setData([row, ...dataRef.current!], currentCount + 1);
      }
    }
  }

  /**
   * Loads list data from current list settings. Also makes sure all columns for which filtering is applied for are visible.
   *
   * @return {*} 
   */
  async function loadDataWithCurrentFilterSettings() {
    if (!search) {
      return;
    }

    // const searchColumns = [search.general1, search.general2, search.date, search.location1, search.location2, search.number].filter(x => !!x);
    // if (tableRef && tableRef.current && searchColumns.length > 0) {
    //   tableRef.current.makeSureColumnsAreVisible(searchColumns);
    // }

    await fetchData(appContext.api, search);
  }

  /**
   * Calls list view implementation to get the actual data based on given search object
   *
   * @param {*} api
   * @param {*} search
   */
  async function fetchData(api:any, search:any) {
    const orderBy = listFilterOrder ? (listOrderBy ? listOrderBy : listView.getDefaultOrderBy()) : listView.getDefaultOrderBy();
    setListOrderBy(orderBy);
    const dataFetchResult = await listView.fetchAndFormatData(api, search, opType, appContext.user, jobListViewContext);
    setData(dataFetchResult.data, dataFetchResult.totalCount);

    if (!f6Query && !isFilterObjectEmpty(search) && dataFetchResult.data.length <= 0) {
      await showGlobalOkDialog(<p>No data found</p>);
      clearFilters();
      setSelectedId(null);
    }
  }

  /**
   * Callback function for filter clear inside filter window or by clicking on the same menu item again.
   * Reset order by by default
   *
   */
  function onFilterClear() {
    setListOrderBy(listView.getDefaultOrderBy());
  }

  /**
   * Cleanup routing before Job item is being disposed
   *
   */
  function cleanup() {
    //deleteInfoAreaContent();
    deleteHideLogHistory();
    clearListData();
    clearFiltersFromStorage();
    deleteListOrderBy();
  }

  /**
   * Schedule Esc key event handler in scope with current state
   *
   */
  function scheduleEscProcess() {
    // In case any modal window is opened, don't close the job
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }

    setEscScheduled(true);
    return true;
  }
  /**
   * Request screen to be closed
   *
   * @return {*} 
   */
  async function closeScreen() {
    const close = await canGoBack();

    setEscScheduled(false);
    if (!close) {
      return false;
    }
  
    // appContext.setJournalType(0);
    if (jobName == 'btLoadPlan') {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTLoadPlan-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }
    else if (jobName == 'btOrder'){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTOrder-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }
    else if (jobName == 'btLocalDO'){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('BTDo-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }
    else if (jobName == 'OCBO'){
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('OrchidBaro-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('OrchidBaro-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }
    }

    goBack(); 

    return true;
  }

  /**
   * Show column settings dialog
   *
   */
  function initializeColumnsDialog() {
    if (tableRef && tableRef.current) {
      tableRef.current.showColumnSettings();
    }
  }

  /**
   * Helper function to extract current order by settings from search object
   *
   * @return {*} 
   */
  function getOrderBy() {
    // if (!search) {
    //   return listView.getDefaultOrderBy();
    // }

    // return {
    //   orderBy: search.orderBy,
    //   orderByAscending: search.orderByAscending,
    // }
    return listOrderBy;
  }

  /**
   * Request data refresh
   *
   */
  function refresh() {
    reloadData();
  }


  function showPopupRow() {
    if (!listView.hasViewPopup()) {
      return;
    }

    const rowIndex = getSelectedIndex();

    if (dataRef && dataRef.current && rowIndex >=0 && rowIndex < dataRef.current.length) {
      setViewPopupRow(dataRef.current[rowIndex]);
    }
  }

  /**
   *
   * @param {number} rowIndex
   */
  async function onRowDoubleClick(rowIndex:number) {
    // This is globally attached event handler. If Search window is opened - dismiss this call
    const searchWindow = document.getElementById('ifms-list-screen-filter');
    if (searchWindow) {
      return;
    }


    if (dataRef && dataRef.current && rowIndex >= 0 && rowIndex < dataRef.current.length) {
      if (listView.hasViewPopup()) {
        setViewPopupRow(dataRef.current[rowIndex]);
        return;
      }

      let viewParams = listView.getViewUrlFor(dataRef.current[rowIndex]) as ViewUrl;
      if (_.isEmpty(viewParams.url))
        viewParams = { url: listView.getViewUrlFor(dataRef.current[rowIndex]) as string, needAfterScreenUrl: false, checkAllOperation: false };
      
      if (!viewParams.url) {
        let openParams = listView.getOpenUrlFor(dataRef.current[rowIndex]);
        if (!openParams || _.isEmpty(openParams)) {
          return;
        } else {
          let left = (document.body.offsetWidth / 2) - (980 / 2);
          left += window.screenLeft;
  
          window.open(`${config.serverUrl}report/logViewer?jwt=${appContext.api.getAuthKey()}` + openParams,
            '_blank',
            `width=980, height=${window.screen.availHeight},left=${left}, top=0, scrollbars=yes`
          );
        }
        return;
      }

      moveToOpenedScreenInAnyOperationStartingWithUrl(viewParams.url, [viewParams.url, viewParams.url.replace('/view', '/edit')], viewParams.needAfterScreenUrl);
    }
  }

  /**
   * Callback function when table header is being clicked. Change ordering accordingly.
   *
   * @param {*} c
   * @return {*} 
   */
  function onHeaderClick(c:any) {
    if (!c || !c.id || c.id === 'indicator' || !c.sortable) {
      return;
    }

    const orderBy = getOrderBy();

    let newOrder = {orderBy: c.id, orderByAscending: true};

    if (!orderBy || orderBy.orderBy !== c.id) {
      setListOrderBy(newOrder);
    } else {
      newOrder = {orderBy: orderBy.orderBy, orderByAscending: !orderBy.orderByAscending};
      setListOrderBy(newOrder);
    }

    const sortResult = sortListData(dataRef.current ?? [], newOrder, listView.getTableSettings(opType, systemSettings, suntekSettings).columns);
    headerClickRef.current = true;

    if (sortResult.sorted) {
      setData(sortResult.data, sortResult.data.length);
    }
  }

  function exportIntoExcel() {
    const table = document.getElementById(listDomId);
    const rowCount = table?.querySelectorAll('div[role="row"]').length;
    if (rowCount === 1) showGlobalOkDialog(<p>No data to Export</p>);
    else {
      let jobname = job.jobName;
      if (listDomId.includes('invoice-warehousing')) jobname = JobNames.WHInvoice;
      else if (listDomId.includes('bill-warehousing')) jobname = JobNames.WHBill;
      else if (listDomId.includes('crdb-warehousing')) jobname = JobNames.WHCrDb;
      exportDomTableIntoExcel(listDomId, `${operationName}-${title}`, systemSettings?.userId ?? '', jobname, job.generateListView(), appContext)
    }
  }

  function jobListViewBeforePager() {
  // if (appContext.appInfoAreaContent !== infoAreaContent)
    //   setInfoAreaContent(appContext.appInfoAreaContent);
    return (
      <div>
        {/* {additionalCommands &&
          <>
            {additionalCommands}&nbsp;&nbsp;
          </>
        } */}
        {listView.hasMemo() &&
          <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
            <PrimaryButton
              name='list-memoLog'
              size="regular"
              onClick={() => {
                //setHideLogHistory(true);
                setInfoAreaContent(appContext.appInfoAreaContent === 'memo' ? 'none' : 'memo');
              }}
              onMouseOver={()=> isMouseOverRef.current = true}
              onMouseEnter={() => isMouseOverRef.current = true}
              onMouseLeave={() => isMouseOverRef.current = false}
              data-tooltip={constants.Document_Server}
              >
              <div style={{ marginTop: '-4px' }}>
                <span> &nbsp; {constants.Doc_Server} &nbsp;</span>
                <ToggleButton isHovered={isMouseOverRef.current} value={appContext.appInfoAreaContent === 'memo'}
                />
              </div>
            </PrimaryButton>
            {/*&nbsp;
             <BlackInverseButton
              name='list-logHistory'
              size='small'
              data-tooltip="Log History"
              disabled={!listView.useLogHistory()}
              notsetbackcolorwhendisabled={true}
              onClick={() => {
                if (infoAreaContent === 'none') {
                  setHideLogHistory(false);
                  setInfoAreaContent('memo');
                }
                else
                  setHideLogHistory(!hideLogHistory);
              }}
              >
              <img
                height="23px"
                width="23px"
                src={
                  listView.useLogHistory()
                    ? hideLogHistory
                      ? logHistoryOffIcon
                      : logHistoryOnIcon
                    : logHistoryDisabledIcon
                }
                alt="Log History"
                style={{ position: 'relative', top: '-3px' }}
              />
            </BlackInverseButton> */}
          </div>
        }
      </div>
    );
  }

  function jobListViewBeforeConfigureButton() {
    return (
      <>
        {listView.renderBeforeConfigureButton()}
          <BlackInverseButton data-tooltip="Export into Excel" name='export_into_excel_button' size="large" onClick={exportIntoExcel}>
            <img height="18px" src={!appContext.useDarkMode ? excelIcon : excelIconGreen} alt="Export into Excel" />
          </BlackInverseButton>&nbsp;&nbsp;
      </>
    );
  }

  const orderBy = getOrderBy();

  const tableOptions:ApplicableTableSettings = {
    customTableId: listDomId,
    ref: tableRef,
    selectable: false,
    tableName: listDomId,
    ...listView.getTableSettings(opType, systemSettings, suntekSettings),
    height: '2000px',
    data: data.map((x:any) => ({...x})),
    rowRenderers: listView.getRowRenderers(opType, systemSettings, suntekSettings, data.length, appContext.useDarkMode),
    orderBy: [orderBy],
    headersColor: (jobName === JobNames.OrchidBaro) ? 'purple' : 'gray',
    headersFontColor: (jobName === JobNames.OrchidBaro) ? 'white': '',
    isListView: true,
    onHeaderClick,
    useHighlightCellOnMouseover:false,
    rowProps: (row: any) => {
      if(jobName === JobNames.OrchidBaro){
        let backgroundColor = '';
        backgroundColor = StatusBackColor(data[row.index]);
        if (backgroundColor) {
          return {
            style: { backgroundColor }
          };
        }
      }else {
      if (row.index % 2 !== 0) {
        let backgroundColor = '';
        switch(appContext.backgroundColor) {
          case 'W': backgroundColor = '#E4E6EB'; break;
          case 'A': backgroundColor = '#E5FFE5'; break;
          case 'I': backgroundColor = '#FFFFE3'; break;
        }

        if (backgroundColor) {
          return {
            style: { backgroundColor }
          };
        }
      }
      }
      return undefined;
    },
    useSetClickedCellWhenClick:operationName === 'Ocean Import' || operationName === 'Other Shipment' || operationName === 'Manifest',
    onInitialized: () => { setInitialized(true); },
    getSelectedRow: getSelectedRow,
    doSelectTableCellAndNotify: doSelectTableCellAndNotify
  };

  // const topRightMenu = listView.renderInsideTopRightOpMenu(jobListViewContext);

  // const RenderedTable = <SmartTable key={search?.version ?? 0} {...tableOptions} />;

  // Use memo to optimize to not rerender table when nothing related to it changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  
  const RenderedTable = React.useMemo(() => <SmartTable key={search?.version ?? 0} { ...tableOptions } />,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [dataRef.current, search, initialized, tick]);
  //const RenderedTable = React.useMemo(() => <SmartTable key={search?.version ?? 0} {...tableOptions} />, [dataRef.current]);
  // const RenderedTable = React.useMemo(() => <SmartTable key={search?.version ?? 0} {...tableOptions} />, [search, dataRef.current]);

  const additionalCommands = listView.renderAdditionalCommands();
  const serachMargin = listView.transactionImpactBtn(appContext.user?.id??'') === null ? '0 10px 0 0' : '0 10px';

  return (
    <JobListViewContext.Provider value={jobListViewContext}>
      <div style={{width: '100%'}} className={listDomWrapperId}>
        <ScreenTitleBar
          resizeTitleLabel={listView.resizetitleLabel()}
          useDarkMode={appContext.useDarkMode}
          refresh={refresh}
          title={title}
          beforePager={jobListViewBeforePager()}
          commandsAfterMemoLogBtn={
            <>
              {listView.commandsAfterMemoLogBtn()}
              <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"}>
                {listView.transactionImpactBtn(appContext.user?.id ?? '')}
                <div style={{ margin: serachMargin }}><FilterButton /></div>
                <CloseButton name="list-close-button" onClick={closeScreen} />
              </div>
              {listView.commandsAfterCloseBtn()}
            </>
          }
          initializeColumnsDialog={initializeColumnsDialog}
          rightBeforeConfigureButton={jobListViewBeforeConfigureButton()}
          rightAfterConfigureButton={
            <div className="ifms-common-no-vertical-separator">
              {additionalCommands &&
                <>
                  {additionalCommands}
                </>
              }
            </div>
          }
        >
          {listView.renderCommands(appContext)}
        </ScreenTitleBar>

        {RenderedTable}

        {listView.renderExtras(hideLogHistory)}
        {listView.renderRowExtras(rowExtrasId, jobListViewContext, clickedCellRef.current, appContext)}
      </div>

      <div id='last-selectedIndex' hidden>{selectedIndex ?? 0}</div>
      <div id='last-selectedId' hidden>{selectedElement?.id ?? 0}</div>
      <div id='last-selectedOiMblId' hidden>{selectedElement?.oiMblId ?? 0}</div>
      <div id='last-selectedRlsStat' hidden>{selectedElement?.rlsStat ?? ''}</div>
      <RightClickContextMenu />
    </JobListViewContext.Provider>
  );
}

const JobListView = () => {
  const location = useLocation();
  let parentOp = '';

  if(location.search && location.search.indexOf('parentOp') > -1) {
    parentOp = location.search.replace('?parentOp=', '');
  }

  const { jobName, opType } = useParams<JobListViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
  */
  return (
    <JobListViewImpl jobName={jobName} opType={opType} parentOp={parentOp} key={`${jobName}-${opType ?? 'common'}-list`} />
  );
};
export default JobListView;


